import {Component, OnInit, OnDestroy} from '@angular/core';
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationApiService, Notification } from 'src/app/services/notification-api.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-nav-right',
  templateUrl: './nav-right.component.html',
  styleUrls: ['./nav-right.component.scss'],
  providers: [NgbDropdownConfig]
})
export class NavRightComponent implements OnInit, OnDestroy {
  currentUser: any = null;
  showProfilePopup = false;
  notifications: Notification[] = [];
  unreadCount: number = 0;
  loadingNotifications = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationApiService
  ) { }

  ngOnInit() {
    // Subscribe to current user changes
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
    
    // Refresh user data from token to ensure it's loaded
    this.authService.refreshUserFromToken();

    // Load notifications
    this.loadNotifications();
    this.loadUnreadCount();

    // Subscribe to unread count changes
    const unreadCountSub = this.notificationService.unreadCount$.subscribe(count => {
      this.unreadCount = count;
    });
    this.subscriptions.push(unreadCountSub);

    // Refresh notifications every 30 seconds
    setInterval(() => {
      this.loadNotifications();
      this.loadUnreadCount();
    }, 30000);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Load notifications
   */
  loadNotifications(): void {
    this.loadingNotifications = true;
    this.notificationService.getNotifications({ 
      status: 'unread',
      per_page: 10 
    }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.notifications = response.data.notifications || [];
        }
        this.loadingNotifications = false;
      },
      error: (error) => {
        console.error('Error loading notifications:', error);
        this.loadingNotifications = false;
      }
    });
  }

  /**
   * Load unread count
   */
  loadUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.unreadCount = response.data.unread_count || 0;
          this.notificationService.updateUnreadCount(this.unreadCount);
        }
      },
      error: (error) => {
        console.error('Error loading unread count:', error);
      }
    });
  }

  /**
   * Mark notification as read
   */
  markAsRead(notification: Notification): void {
    this.notificationService.markAsRead(notification.id).subscribe({
      next: (response) => {
        if (response.success) {
          notification.status = 'read';
          notification.read_at = new Date().toISOString();
          this.loadUnreadCount();
        }
      },
      error: (error) => {
        console.error('Error marking notification as read:', error);
      }
    });
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: (response) => {
        if (response.success) {
          this.notifications.forEach(notif => {
            notif.status = 'read';
            notif.read_at = new Date().toISOString();
          });
          this.loadUnreadCount();
        }
      },
      error: (error) => {
        console.error('Error marking all as read:', error);
      }
    });
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notificationService.clearAll().subscribe({
      next: (response) => {
        if (response.success) {
          this.notifications = [];
          this.loadUnreadCount();
        }
      },
      error: (error) => {
        console.error('Error clearing notifications:', error);
      }
    });
  }

  /**
   * Get time ago string
   */
  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }

  /**
   * Get priority badge class
   */
  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'critical': return 'badge-danger';
      case 'high': return 'badge-warning';
      case 'medium': return 'badge-info';
      default: return 'badge-secondary';
    }
  }

  /**
   * Get user display name
   */
  getUserDisplayName(): string {
    return this.authService.getUserDisplayName();
  }

  /**
   * Get user profile image URL
   */
  getUserProfileImage(): string {
    return this.authService.getUserProfileImage();
  }

  /**
   * Get user employee code
   */
  getUserEmployeeCode(): string {
    return this.authService.getUserEmployeeCode();
  }

  /**
   * Get user role name
   */
  getUserRoleName(): string {
    return this.authService.getUserRoleName();
  }

  /**
   * Check if user has profile image
   */
  hasProfileImage(): boolean {
    const user = this.authService.getCurrentUser();
    return !!(user && user.employee && user.employee.profile_photo);
  }

  /**
   * Get user initials
   */
  getUserInitials(): string {
    const user = this.authService.getCurrentUser();
    if (!user || !user.employee || !user.employee.name) return '?';
    
    const name = user.employee.name.trim();
    const words = name.split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }


  /**
   * Handle profile image error
   */
  onProfileImageError(event: any) {
    // Hide the image - the initials will automatically show due to *ngIf condition
    event.target.style.display = 'none';
  }

  onLogout() {
    this.authService.logout();
    this.router.navigate(['/']);
  }

  /**
   * Open profile popup
   */
  openProfilePopup() {
    this.showProfilePopup = true;
  }

  /**
   * Close profile popup
   */
  closeProfilePopup() {
    this.showProfilePopup = false;
  }

  /**
   * Navigate to workflow help page
   */
  openWorkflowModal() {
    this.router.navigate(['/help']);
  }

  /**
   * Navigate to threshold settings page
   */
  openSettings() {
    this.router.navigate(['/products/threshold-settings']);
  }
}
